import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import CloseIcon from "@mui/icons-material/Close";
import Image from "next/image";

import { useCurrencyCode } from "../helper-functions/CurrencySymbol";
import { ApiService } from "@/app/services/AppService";
import { ResponseObject } from "@/app/classes/main";
import { Button, Grid, Skeleton } from "@mui/material";
import AddToCartButton from "./AddToCartButton";

interface MakeYourOwnPackageProps {
  open: boolean;
  close: () => void;
  sid: any;
}

const MakeYourOwnPackage: React.FC<MakeYourOwnPackageProps> = ({
  open,
  close,
  sid,
}) => {
  const [makeYourOwnPackageData, setMakeYourOwnPackageData] = React.useState<
    any[]
  >([]);
  const currencyCode = useCurrencyCode();
  const apiService = React.useMemo(() => new ApiService(), []);
  const [checkedStates, setCheckedStates] = React.useState<
    Record<string, boolean>
  >({});
  const [checkedBoxData, setCheckedBoxData] = React.useState([]);
  const [reloadCartButton, setReloadCartButton] = React.useState(false);

  const handleBoxClick = (service: any) => {
    setCheckedStates((prev: any) => ({
      ...prev,
      [service.id]: !prev[service?.id],
    }));
    setCheckedBoxData((prev: any) => {
      const isChecked = checkedStates[service.id];
      if (isChecked) {
        return prev.filter((item: any) => item.id !== service.id);
      } else {
        return [...prev, service];
      }
    });
    setReloadCartButton(true);
  };

  const getMakeYourOwnPackage = React.useCallback(async () => {
    try {
      const responseObject = await apiService.get<ResponseObject>(
        apiService.makeYourOwnPackage + "gcode=0&sid=" + sid
      );
      if (responseObject?.status === "success") {
        setMakeYourOwnPackageData(responseObject?.object);
      }
    } catch (error) {
      console.log("Error:", error);
    }
  }, [sid, apiService]);

  React.useEffect(() => {
    if (sid && open) {
      getMakeYourOwnPackage();
    }
  }, [sid, getMakeYourOwnPackage, open]);

  const scrollToSection = (name: any) => {
    const element = document.getElementById(name);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      anchor="bottom"
      sx={{
        zIndex: 10000,
      }}
      PaperProps={{
        sx: {
          background: "none",
          boxShadow: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        },
      }}
    >
      {/* Close Button */}
      <Box sx={{ textAlign: "center", margin: "1rem auto" }}>
        <IconButton
          onClick={close}
          sx={{
            background: "white",
            "&:hover": {
              background: "#f2efefcf",
            },
          }}
        >
          <CloseIcon sx={{ cursor: "pointer", color: "black" }} />
        </IconButton>
      </Box>

      {/* Package Content */}
      {makeYourOwnPackageData && makeYourOwnPackageData?.length > 0 ? (
        <Box
          sx={{
            background: "white",
            boxShadow:
              "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
            borderRadius: "1rem 1rem 0 0",
            padding: "0px 1rem 1rem",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "left",
              alignItems: "center",
              overflow: "scroll",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              padding: { xs: "1rem 0px", sm: "1rem" },
              background: "#fff",
              zIndex: 100,
              width: "-webkit-fill-available",
              position: "sticky",
              top: 0,
            }}
          >
            {makeYourOwnPackageData.map((item: any, index: any) => (
              <Box
                key={index}
                sx={{ marginRight: { xs: "0.5rem", sm: "1rem" } }}
              >
                <Button
                  variant="outlined"
                  color="success"
                  sx={{
                    textWrap: "nowrap",
                    textTransform: "capitalize",
                    background: "#fff",
                    color: "#000",
                  }}
                  onClick={() =>
                    scrollToSection(
                      item?.name.replace(/\s+/g, "-").toLowerCase()
                    )
                  }
                >
                  {item?.name}
                </Button>
              </Box>
            ))}
          </Box>
          {makeYourOwnPackageData.map((item: any, index: any) => (
            <Box
              key={index}
              sx={{ marginBottom: "1rem" }}
              id={item?.name.replace(/\s+/g, "-").toLowerCase()}
            >
              {/* Main Item Heading */}
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", marginBottom: "1rem" }}
              >
                {item?.name}
              </Typography>

              {/* Nested Items */}
              {item?.serviceHeadings?.map((heading: any, headingIndex: any) => (
                <Box key={headingIndex} sx={{ marginBottom: "1rem" }}>
                  {/* Sub Heading */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#808080",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {heading?.name}
                  </Typography>

                  {/* Services */}
                  <Grid container xs={12} sm={12} md={12} lg={12} margin="auto">
                    {heading?.services?.map(
                      (service: any, serviceIndex: any) => (
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={6}
                          lg={6}
                          key={serviceIndex}
                        >
                          <Box
                            onClick={() => handleBoxClick(service)}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "1rem",
                              marginRight: { xs: "0px", sm: "1rem" },
                              cursor: "pointer",
                              maxWidth: { xs: "100%", sm: "90%" },
                            }}
                          >
                            {/* Service Details */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {/* Service Image */}
                              <Image
                                src={service?.coverImage?.secureHttpPath || ""}
                                alt="service-icon"
                                style={{
                                  objectFit: "cover",
                                  height: "65px",
                                  width: "65px",
                                  borderRadius: "0.5rem",
                                }}
                                width={500}
                                height={500}
                              />
                              <Box sx={{ marginLeft: "1rem" }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  {service?.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#808080", fontSize: "13px" }}
                                >
                                  {service?.subHeading}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginTop: "0.5rem",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#15741A",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {currencyCode}
                                    {service?.amount}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "gray",
                                      fontWeight: "bold",
                                      textDecoration: "line-through",
                                      marginLeft: "0.5rem",
                                    }}
                                  >
                                    {currencyCode}
                                    {service?.mrp}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            {/* Checkbox */}
                            <Checkbox
                              color="success"
                              checked={checkedStates[service.id] || false}
                              onChange={() => handleBoxClick(service)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Box>
                        </Grid>
                      )
                    )}
                  </Grid>
                </Box>
              ))}
            </Box>
          ))}
          <AddToCartButton
            data={checkedBoxData}
            reloadCartButton={reloadCartButton}
            setReloadCartButton={() => setReloadCartButton(false)}
          />
        </Box>
      ) : (
        <Box
          sx={{
            background: "white",
            boxShadow:
              "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
            borderRadius: "1rem 1rem 0 0",
            padding: "0px 1rem 1rem",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
            },
            gap: "0rem",
          }}
        >
          {Array.from({ length: 10 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                margin: "0.5rem",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "left",
                  alignItems: "center",
                }}
              >
                <Skeleton
                  variant="rounded"
                  sx={{
                    margin: "0.5rem",
                    width: "55px",
                    height: "55px",
                  }}
                />
                <Skeleton
                  variant="rectangular"
                  sx={{
                    margin: "0.5rem",
                    width: "80%",
                    height: "55px",
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Drawer>
  );
};

export default MakeYourOwnPackage;
